import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { UserGroupDataDisplay } from "#src/components/Common/UserGroupDataDisplay";
import { useListUserGroups } from "#src/components/hooks/adapters/useUserGroups";
import { useListUsers } from "#src/components/hooks/adapters/useUsers";
import { useHasPermission } from "#src/contexts/AuthenticatedContext.helpers";
import {
  Accordion,
  ArrayDataDisplay,
  FilterDisplay,
  KeyValueList,
  KeyValuePairList,
  TextDataDisplay,
} from "@validereinc/common-components";
import { UserGroupType, UserType } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import { SEND_REPORT_ACTION_FILTER_BUILDER_FIELDS } from "../WorkflowConfigurationDrawer/Forms/WorkflowConfigurationSendReportDetails";
import { parseBackendFilterToFilterBuilderFormat } from "../WorkflowConfigurationDrawer/Utils/WorkflowConfigurationUpdateRecordStatusDetails.helpers";
import styles from "./WorkflowActionSendReport.module.scss";

const cx = classNames.bind(styles);

const { AccordionPanel } = Accordion;

export const WorkflowActionSendReport = ({
  users,
  groups,
  message,
  title,
  reportId,
  reportFilter,
}: {
  users: string[];
  groups: string[];
  message: string;
  title: string;
  reportId?: string;
  reportFilter?: Record<string, any>;
}) => {
  const [canReadUserGroups] = useHasPermission("user_groups:read");
  const usersQuery = useListUsers({
    filters: {
      id: {
        $in: users,
      },
    },
  });
  const groupsQuery = useListUserGroups(
    {
      filters: {
        id: {
          $in: groups,
        },
      },
    },
    { enabled: canReadUserGroups && groups.length > 0 }
  );
  const usersMap = useMemo(() => {
    const users = usersQuery.data?.data ?? [];
    return users.reduce<Record<string, UserType>>((map, user) => {
      map[user.id] = user;
      return map;
    }, {});
  }, [usersQuery]);
  const groupsMap = useMemo(() => {
    const groups = groupsQuery.data?.data ?? [];
    return groups.reduce<Record<string, UserGroupType>>((map, group) => {
      map[group.id] = group;
      return map;
    }, {});
  }, [groupsQuery]);
  const parsedFilter = useMemo(() => {
    if (reportFilter) {
      return parseBackendFilterToFilterBuilderFormat(reportFilter) as Record<
        string,
        any
      >;
    } else return undefined;
  }, [reportFilter]);
  const contentData: KeyValuePairList = [
    {
      title: "Subject",
      value: <TextDataDisplay value={title} />,
    },
    {
      title: "Body",
      value: <TextDataDisplay value={message} />,
    },
  ];
  const recipientsData: KeyValuePairList = [
    {
      title: "Users",
      value: (
        <ArrayDataDisplay
          value={users.map((id) => (
            <UserDataDisplay
              key={id}
              id={id}
              name={usersMap[id]?.name}
              isInline
            />
          ))}
          isLoading={usersQuery.isLoading}
        />
      ),
    },
    {
      title: "Groups",
      value: (
        <ArrayDataDisplay
          value={groups.map((groupId) => (
            <UserGroupDataDisplay
              key={groupId}
              id={groupId}
              name={groupsMap[groupId]?.name}
              isInline
            />
          ))}
          isLoading={groupsQuery.isLoading}
        />
      ),
    },
  ];
  const reportData: KeyValuePairList = [
    {
      title: "Report ID",
      value: <TextDataDisplay value={reportId} />,
    },
  ];

  return (
    <Accordion defaultActiveKeys={["report", "content", "recipients"]}>
      <AccordionPanel
        dataKey="report"
        title="Report to Send"
      >
        {parsedFilter && (
          <FilterDisplay
            filter={parsedFilter}
            fields={SEND_REPORT_ACTION_FILTER_BUILDER_FIELDS}
            variant="vertical"
            className={cx("filter-display")}
          />
        )}
        {reportId && (
          <KeyValueList
            className={cx("keyValueListContainer")}
            data={reportData}
          />
        )}
      </AccordionPanel>
      <AccordionPanel
        dataKey="content"
        title="Content"
      >
        <KeyValueList
          className={cx("keyValueListContainer")}
          data={contentData}
        />
      </AccordionPanel>
      <AccordionPanel
        dataKey="recipients"
        title="Recipients"
      >
        <KeyValueList
          className={cx("keyValueListContainer")}
          data={recipientsData}
        />
      </AccordionPanel>
    </Accordion>
  );
};
